import { aktion } from "./nutzung";
import { GEAENDERT_FELDER, type GeaendertFeld, type Vorschlag, type VorschlagGeaendert } from "./typen";
/** Duenner Client fuer die Papiergaist-API. Alle Aufrufe laufen ueber /api (gleicher Ursprung). */

export type DienstStatus = { ok: boolean; ms: number; fehler?: string } & Record<string, unknown>;

/** Im Betrieb meldet der Server nach aussen nur `ok`; Version und Dienste gibt es nur in Entwicklung. */
export type Gesundheit = {
  ok: boolean;
  version?: string;
  dienste?: Record<string, DienstStatus>;
};

export async function holeGesundheit(signal?: AbortSignal): Promise<Gesundheit> {
  const antwort = await fetch("/api/gesundheit", { signal, headers: { Accept: "application/json" } });
  // 503 traegt denselben Bericht; nur andere Codes sind ein echter Fehler.
  if (!antwort.ok && antwort.status !== 503) {
    throw new Error(`Server antwortet mit ${antwort.status}`);
  }
  return (await antwort.json()) as Gesundheit;
}

/** Fehler der API mit Status und lesbarer deutscher Meldung (fuer die Oberflaeche). */
export class ApiFehler extends Error {
  readonly status: number;
  readonly daten: unknown;

  constructor(status: number, meldung: string, daten?: unknown) {
    super(meldung);
    this.status = status;
    this.daten = daten;
  }
}

/** W47i: erster Satz des Servers (`VORSCHLAG_GEAENDERT`), wenn der Koerper keinen Text traegt. */
export const VORSCHLAG_GEAENDERT_TEXT = "Der Vorschlag hat sich geändert, bitte noch einmal ansehen.";
/** Muster der Kennung wie im Server (`VorschlagKennung`). */
const KENNUNG = /^(ohne|[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/;

/** W47i: der 409 `VORSCHLAG_GEAENDERT` von „Passt“ oder „Ablegen“, sonst null (auch der 409 „Dokument ist noch nicht
 *  bereit.“). Robust gegen einen kaputten Koerper: ohne gueltige Kennung null (ohne sie ginge das naechste Ablegen ohne
 *  Pruefung durch, darum bleibt es dann ein gewoehnlicher Fehler); `geaendert` kein Array zaehlt als null, unbekannte
 *  Schluessel fallen weg; `vorschlag` kein Objekt zaehlt als null. */
export function vorschlagGeaendert(fehler: unknown): VorschlagGeaendert | null {
  if (!(fehler instanceof ApiFehler) || fehler.status !== 409) return null;
  const d = fehler.daten;
  if (!d || typeof d !== "object" || Array.isArray(d)) return null;
  const k = d as Record<string, unknown>;
  if (k.code !== "VORSCHLAG_GEAENDERT" || typeof k.vorschlag_id !== "string" || !KENNUNG.test(k.vorschlag_id)) return null;
  const bekannt = new Set<unknown>(GEAENDERT_FELDER);
  const geaendert = Array.isArray(k.geaendert) ? GEAENDERT_FELDER.filter((f): f is GeaendertFeld => bekannt.has(f) && (k.geaendert as unknown[]).includes(f)) : null;
  const vorschlag = k.vorschlag && typeof k.vorschlag === "object" && !Array.isArray(k.vorschlag) ? (k.vorschlag as Vorschlag) : null;
  return {
    detail: typeof k.detail === "string" && k.detail.trim() ? k.detail : VORSCHLAG_GEAENDERT_TEXT,
    code: "VORSCHLAG_GEAENDERT",
    vorschlag_id: k.vorschlag_id,
    vorschlag,
    geaendert,
  };
}

// Das CSRF-Token kommt mit jeder Sitzungsantwort (/auth/ich, /auth/anmelden, ...) und geht bei
// jeder schreibenden Anfrage im Kopf mit (der Server vergleicht es mit seinem Cookie).
let csrf = "";

export function setzeCsrf(token: string): void {
  csrf = token;
}

/** Fuer Anfragen ausserhalb von api() (Antworten des Gaists als Strom). */
export function holeCsrf(): string {
  return csrf;
}

// W20g: Versatz der Geraeteuhr zur Uhr des Servers, aus dem Date-Kopf der letzten Antwort. Zeitpunkte
// aus dem Server (etwa `nachweis_frisch_bis`) werden damit gegen die Serverzeit verglichen.
let versatz: number | undefined;

/** Merkt den Versatz zum Server; ohne lesbaren Date-Kopf ist die Serverzeit unbekannt. */
export function uhrAbgleichen(datum: string | null, jetzt: number = Date.now()): void {
  const server = datum ? Date.parse(datum) : NaN;
  versatz = Number.isNaN(server) ? undefined : server - jetzt;
}

/** Jetzt nach der Uhr des Servers (auf die Sekunde), `undefined` ohne Date-Kopf. */
export function serverJetzt(jetzt: number = Date.now()): number | undefined {
  return versatz === undefined ? undefined : jetzt + versatz;
}

type Optionen = {
  methode?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  json?: unknown;
  formular?: FormData;
  signal?: AbortSignal;
};

export async function api<T>(pfad: string, optionen: Optionen = {}): Promise<T> {
  const { json, formular, signal } = optionen;
  const methode = optionen.methode ?? (json !== undefined || formular ? "POST" : "GET");
  const kopf: Record<string, string> = { Accept: "application/json" };
  let koerper: BodyInit | undefined;
  if (json !== undefined) {
    kopf["Content-Type"] = "application/json";
    koerper = JSON.stringify(json);
  } else if (formular) {
    koerper = formular;
  }
  if (methode !== "GET") kopf["X-CSRF-Token"] = csrf;
  const antwort = await fetch(`/api${pfad}`, {
    method: methode,
    headers: kopf,
    body: koerper,
    signal,
    credentials: "same-origin",
  });
  uhrAbgleichen(antwort.headers.get("Date"));
  if (antwort.ok) aktion(methode, pfad); // W15: jede gelungene schreibende Anfrage zaehlt als Aktion
  if (antwort.status === 204) return undefined as T;
  const daten: unknown = await antwort.json().catch(() => null);
  if (!antwort.ok) throw new ApiFehler(antwort.status, meldung(antwort.status, daten), daten);
  return daten as T;
}

/** Klartext statt Systemsprache (Designsprache Leitsatz 8). */
export function meldung(status: number, daten: unknown): string {
  const detail = (daten as { detail?: unknown } | null)?.detail;
  // W82 Web-11a W11A-2 (ZP Web-10b B-6): ein Satz aus Leerraum ist kein Satz; dann der Satz des Status
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    // W82 Web-10b W10B-4 (R1:A4d): ein Eintrag vom Typ „hinweis“ trägt einen Satz für Menschen (Passwortregel, Notiz zum
    // Brief), Muster `notizSatz` in FreigabeSheet; jeder andere Eintrag bleibt beim Standardsatz
    const hinweis = (detail as { type?: unknown; msg?: unknown }[]).find((e) => e?.type === "hinweis" && typeof e.msg === "string" && e.msg.trim());
    return hinweis ? String(hinweis.msg) : "Bitte die Eingaben prüfen.";
  }
  if (status === 401) return "Bitte melde dich an.";
  if (status === 403) return "Das ist nicht erlaubt.";
  if (status === 404) return "Nicht gefunden.";
  if (status === 413) return "Die Datei ist zu groß.";
  if (status === 429) return "Zu viele Versuche. Bitte warte kurz.";
  if (status >= 500) return "Der Server hat gerade ein Problem. Bitte später noch einmal.";
  return `Unerwartete Antwort (${status}).`;
}

/** Text fuer eine fehlgeschlagene Aktion (Toast): Antwort der API oder „keine Verbindung“. */
/** Bei erreichter Plan-Grenze (402) der Weg zur Plan-Seite fuer Toasts (Verifier W9, W1). */
export function planWeg(fehler: unknown): { text: string; zu: string } | undefined {
  return fehler instanceof ApiFehler && fehler.status === 402 ? { text: "Plan ansehen", zu: "/plan" } : undefined;
}

export function fehlerText(fehler: unknown): string {
  return fehler instanceof ApiFehler ? fehler.message : "Keine Verbindung. Bitte später noch einmal.";
}

/** W20: Klartext fuer eine fehlgeschlagene Anfrage nach einem Anmelde-Link. Die Grenzen (429) zaehlen
 *  jede Anfrage, mit oder ohne Konto: die Meldung sagt daher nichts ueber die Adresse. */
export function linkFehlerText(fehler: unknown): string {
  if (fehler instanceof ApiFehler && fehler.status === 429)
    return "Du hast gerade mehrmals einen Link angefordert. Warte bitte etwas und versuch es dann noch einmal.";
  return fehlerText(fehler);
}
